/**
 * LanguageSwitcher — FR / EN selector.
 *
 * Two looks, one behaviour:
 *   - variant="select" (default): header style, same chrome as the OrgSwitcher next to it.
 *   - variant="pills": compact pill group for dark backgrounds (login page).
 *
 * The choice is persisted in localStorage.fm_lang and, when a session exists,
 * pushed to the user profile (PUT /auth/preferences { language }) so it follows
 * the user across browsers. The UI never waits for the server.
 */

import React from 'react';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, setLanguage } from '../../../i18n/index.js';
import { authAPI } from '../../../api/auth.js';

function persistOnServer(lang) {
  // Only meaningful with a token; authAPI fails silently on 401 and we don't care here.
  try {
    if (!localStorage.getItem('fm_access_token')) return;
    authAPI.updatePreferences({ language: lang }).catch(() => {});
  } catch { /* no storage */ }
}

export function LanguageSwitcher({ variant = 'select', className = '' }) {
  const { t, i18n } = useTranslation();
  const current = i18n.language?.split('-')[0] || 'fr';

  const change = async (code) => {
    if (code === current) return;
    await setLanguage(code);
    persistOnServer(code);
  };

  if (variant === 'pills') {
    return (
      <div className={`inline-flex items-center gap-1 rounded-lg bg-slate-800/60 border border-slate-700/50 p-0.5 ${className}`}
           role="group" aria-label={t('common.language.switchTitle')}>
        {SUPPORTED_LANGUAGES.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => change(l.code)}
            title={l.label}
            className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
              l.code === current ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            {l.short}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 px-2 py-1 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 ${className}`}
         title={t('common.language.switchTitle')} data-tour="language-switcher">
      <Globe className="w-4 h-4 text-gray-500" />
      <select
        value={current}
        onChange={(e) => change(e.target.value)}
        className="bg-transparent text-sm text-gray-800 dark:text-gray-100 outline-none cursor-pointer"
        aria-label={t('common.language.label')}
      >
        {SUPPORTED_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>{l.short}</option>
        ))}
      </select>
    </div>
  );
}

export default LanguageSwitcher;
